import type { TrailRender } from "../../../shared/shop-catalog";
import { customPalette, dominantColor } from "./CustomArt";

interface Pt {
  x: number;
  y: number;
}

// Trails whose look is inherently discrete scattered objects (confetti, snow,
// stars, bubbles…). These keep the particle system; everything else renders as a
// single continuous ribbon.
const PARTICLE_TRAIL_KINDS = new Set<TrailRender["kind"]>([
  "confetti",
  "blizzard",
  "bubble",
  "sparkle",
  "star",
  "heart",
  "blossom",
  "glitch",
  "pixel",
  "candy",
  "cosmic",
  "magic",
  "cyber",
]);

export function isParticleTrail(kind: TrailRender["kind"]): boolean {
  return PARTICLE_TRAIL_KINDS.has(kind);
}

// Kinds that read as "glowy" get a soft bloom on the ribbon.
const GLOW_KINDS = new Set<TrailRender["kind"]>([
  "neon",
  "laser",
  "divine",
  "plasma",
  "cosmic",
  "creator",
  "void",
  "portal",
  "lightning",
  "magic",
  "aurora",
  "gold",
  "rainbow",
]);

/**
 * A continuous trail that follows the sprite as one unbroken line: every tick a
 * point is recorded at the sprite's tail, existing points scroll left with the
 * world, and the whole history is stroked as a single smooth path. Because the
 * newest point sits on the sprite, the line never detaches or breaks into gaps.
 */
export class TrailRibbon {
  private readonly pts: Pt[] = [];
  private readonly max = 110;

  reset(): void {
    this.pts.length = 0;
  }

  /** Scroll the recorded path left so it trails behind in world space. */
  update(dt: number, scrollSpeed: number): void {
    const dx = scrollSpeed * dt;
    for (const p of this.pts) p.x -= dx;
    while (this.pts.length > 1 && this.pts[1]!.x < -40) this.pts.shift();
  }

  /** Record the sprite's current tail position as the new head of the line. */
  push(x: number, y: number): void {
    this.pts.push({ x, y });
    if (this.pts.length > this.max) this.pts.shift();
  }

  render(ctx: CanvasRenderingContext2D, cfg: TrailRender): void {
    if (this.pts.length < 2) return;
    const w = clamp(cfg.sizeMax * 1.5, 2.5, 9);
    ctx.save();
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth = w;
    ctx.strokeStyle = this.strokeStyle(ctx, cfg);
    if (GLOW_KINDS.has(cfg.kind)) {
      ctx.shadowColor = cfg.color2 ?? cfg.color;
      ctx.shadowBlur = 6;
    }
    // Smooth the polyline with midpoint quadratics so the line reads as a single
    // flowing ribbon rather than a jagged chain.
    ctx.beginPath();
    const p0 = this.pts[0]!;
    ctx.moveTo(p0.x, p0.y);
    for (let i = 1; i < this.pts.length - 1; i += 1) {
      const a = this.pts[i]!;
      const b = this.pts[i + 1]!;
      ctx.quadraticCurveTo(a.x, a.y, (a.x + b.x) / 2, (a.y + b.y) / 2);
    }
    const last = this.pts[this.pts.length - 1]!;
    ctx.lineTo(last.x, last.y);
    ctx.stroke();
    ctx.restore();
  }

  private strokeStyle(ctx: CanvasRenderingContext2D, cfg: TrailRender): string | CanvasGradient {
    const a = this.pts[0]!;
    const b = this.pts[this.pts.length - 1]!;
    if (cfg.kind === "rainbow" || cfg.kind === "creator") {
      const t = performance.now() / 1000;
      const g = ctx.createLinearGradient(a.x, a.y, b.x, b.y);
      for (let i = 0; i <= 6; i += 1) g.addColorStop(i / 6, `hsl(${(i * 60 + t * 120) % 360}, 85%, 60%)`);
      return g;
    }
    if (cfg.kind === "custom") {
      const pal = cfg.cells && cfg.cells.length ? customPalette(cfg.cells) : [];
      if (pal.length >= 2) {
        const g = ctx.createLinearGradient(a.x, a.y, b.x, b.y);
        pal.forEach((c, i) => g.addColorStop(i / (pal.length - 1), c));
        return g;
      }
      return pal[0] ?? dominantColor(cfg.cells ?? []);
    }
    if (cfg.color2 && cfg.color2 !== cfg.color) {
      const g = ctx.createLinearGradient(a.x, a.y, b.x, b.y); // tail → head
      g.addColorStop(0, cfg.color2);
      g.addColorStop(1, cfg.color);
      return g;
    }
    return cfg.color;
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
